<template>
    <div class="public-user-reports-wrapper">
        <div class="public-user-reports-bg">
            <div class="public-user-reports-header-container">
                <div class="public-user-reports-header-container__text">
                    <h1>Moje zgłoszenia</h1>
                </div>
                <div class="public-user-reports-header-container__img"></div>
            </div>
        </div>
        <div class="public-user-reports-table">
            <div class="public-user-reports-table-search-criteria">
                <EonTimePicker :width="width" name="picker" label="Data zgłoszenia" :show-time="false"
                               v-model="searchCriteria.reportDate" @update:modelValue="search" showLabel/>
            </div>
            <DataTable :value="myReports" lazy
                       :search-criteria="searchCriteria" @page="onPage" @sort="onPage"
                       :loading="loading" removableSort
                       class="reports-table"
                       paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink
                       LastPageLink CurrentPageReport RowsPerPageDropdown"
                       currentPageReportTemplate="Elementy na stronie"
                       responsiveLayout="scroll" style="padding-bottom: 50px;">
                <Column header="Adres">
                    <template #body="slotProps">
                        {{ computedAddress(slotProps.data) }}
                    </template>
                </Column>
                <Column header="Data zgłoszenia" field="reportDate" sortable />
                <Column header="Treść" field="comment" />
            </DataTable>
        </div>
    </div>
</template>

<script>
    import DataTable from "primevue/datatable";
    import Column from "primevue/column";
    import EonInputText from "@/components/form/EonInputText";
    import EonTimePicker from "@/components/form/EonTimePicker";
    import {getUserReports} from "@/swagger/vue-api-client";
    import {SearchCriteria} from "@/utils/SearchCriteria";
    import {TimeUtils} from "@/utils/TimeUtils";

    export default {
        name: "PublicUserReportsView",

        components: {
            DataTable,
            Column,
            EonInputText,
            EonTimePicker,
        },

        emits: ["update:searchCriteria"],

        data() {
            return {
                loading: false,
                myReports: [],
                searchCriteria: {
                    reportDate: "",
                    sortField: null,
                    sortOrder: null,
                },
                fontSize: document.documentElement.getAttribute("data-font-size"),
                fontSizeObserver: null,
            };
        },

        mounted() {
            this.onPage({});
            document.querySelectorAll("table thead tr th").forEach((th) => {
                th.setAttribute("scope", "col");
            });

            const observer = new MutationObserver(() => {
                this.fontSize = document.documentElement.getAttribute("data-font-size");
            });

            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ["data-font-size"],
            });

            this.fontSizeObserver = observer;
        },

        beforeUnmount() {
            if (this.fontSizeObserver) {
                this.fontSizeObserver.disconnect();
            }
        },

        methods: {
            search() {
                this.onPage({});
            },

            onPage(event) {
                this.loading = true;

                this.searchCriteria.sortField = event.sortField;
                this.searchCriteria.sortOrder = event.sortOrder;
                if (this.searchCriteria.reportDate != null
                    && typeof this.searchCriteria.reportDate === "object") {
                    const utcDate = new Date(this.searchCriteria.reportDate.getTime()
                        - (this.searchCriteria.reportDate.getTimezoneOffset() * 60000));

                    this.searchCriteria.reportDate = new Date(utcDate.toISOString().split("T")[0]);
                }

                getUserReports({body: this.searchCriteria})
                    .then((response) => {
                        this.myReports = response.data;
                        this.myReports.forEach((report) => {
                            report.reportDate = new Date(report.reportDate)
                                .toLocaleString("pl-PL", TimeUtils.options);
                        });
                        this.loading = false;
                    });

                SearchCriteria.updateUrl(this);
            },

            computedAddress(address) {
                const streetType = address.streetType ? (address.streetType + " ") : "";
                return streetType + address.streetName + " " + address.houseNumber + ", " + address.city;
            },
        },

        computed: {
            width() {
                return this.fontSize === "2" ? "560" : "280";
            },
        },
    };
</script>

<style lang="less">
@import "@/assets/theme/variable.less";
.public-user-reports-wrapper {
    padding-top: 4.5rem;

    @media screen and (max-width: 800px) {
        padding-top: 3.75rem;
    }
    .public-user-reports-bg {
        .public-user-reports-header-container {
            width: 100%;
            height: 300px;
            display: flex;

            @media screen and (max-width: 800px) {
                height: auto;
            }
            &__text {
                background-color: @tint-bordeaux-00;
                width: 48%;
                padding: 50px 30px 50px 140px;
                display: flex;
                align-items: center;
                justify-content: center;
                @media screen and (max-width: 1250px) {
                    padding: 50px 30px 50px 80px;
                }
                @media screen and (max-width: 700px) {
                    width: 100%;
                    padding: 50px;
                }
                h1 {
                    color: white;
                    font-size: 5rem;
                    font-weight: 900;
                    text-align: right;
                    @media screen and (max-width: 1550px) {
                        font-size: 4.375rem;
                    }
                    @media screen and (max-width: 1398px) {
                        font-size: 3.75rem;
                    }
                    @media screen and (max-width: 1125px) {
                        font-size: 3.125rem;
                    }
                    @media screen and (max-width: 700px) {
                        margin: 0;
                        font-size: 4.125rem;
                    }
                    @media screen and (max-width: 575px) {
                        font-size: 2.25rem;
                    }
                }
            }
            &__img {
                width: 52%;
                background-image: url("../../assets/myReportsBG.png");
                background-size: cover;
                background-position: 0 -120px;
                @media screen and (max-width: 1560px) {
                    background-position: center;
                }
                background-repeat: no-repeat;
                @media screen and (max-width: 700px) {
                    display: none;
                }
            }
        }
    }

    .public-user-reports-table {
        display: flex;
        flex-direction: column;
        align-items: center;
        .public-user-reports-table-search-criteria {
            padding: 2rem 0;
            display: flex;
            gap: 2rem;
            flex-wrap: wrap;
            justify-content: center;
            width: 60%;
            @media screen and (max-width: 620px) {
                width: 80%;
            }
            @media screen and (max-width: 420px) {
                width: 90%;
            }
        }
        .reports-table {
            width: 60%;
            display: flex;
            flex-direction: column;
            gap: 2rem;
            @media screen and (max-width: 620px) {
                width: 80%;
            }
            @media screen and (max-width: 420px) {
                width: 90%;
            }
        }
    }
}
html[data-font-size='2'] {
    .public-user-reports-wrapper {
        .public-user-reports-bg {
            .public-user-reports-header-container {
                height: 400px;
            }
            .public-user-reports-header-container__text {
                h1 {
                    margin-left: 0;
                }
            }
        }
        .public-user-reports-table {
            .reports-table {
                width: 90%;
            }
        }
    }
}
</style>
